import type * as React from "react";
import {
	type ColumnDef,
	flexRender,
	type useReactTable,
} from "@tanstack/react-table";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../ui/table";
import { Outlet } from "react-router-dom";
import type { ISocial } from "../../types/models/social.models.types";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
} from "../ui/select";
import { SelectValue } from "@radix-ui/react-select";
import type { IBrand } from "../../types/models/brands.models.types";
import type { IPagination } from "../../types/models/pagination.models.types";
import PaginationComponent from "../pagination.components";
import type { IPayoutTransactionMinimal } from "../../types/models/payout-transaction.modals.types";
import type { INotificationExtended } from "../../hooks/http/admin/notifications/get-past-notifications.admin.http";
import type { ICSVUpload } from "../../hooks/http/admin/favorites/add-new-csv-favorite-collection.admin.http";
import type { IAddedFixedPayout } from "../../hooks/http/admin/campaigns/get-fixed-payouts.http";
import type { IFixedPayoutSocialsMinimal } from "../../hooks/http/admin/campaigns/add-fixed-payouts-to-campaign.admin.http";
import type { ICreator } from "../../types/models/creator.models.types";
import type { IVendor } from "../../types/models/vendor.modals.types";
import type { IVendorCampaign } from "../../hooks/http/admin/vendors/campaigns/get-all-vendor-campaigns.admin.http";
import { IUser } from "../../types/models/user.models.types";
import { KycRowData } from "../../store/kyc-approvals.store";

interface AppProps {
	table: ReturnType<typeof useReactTable>;
	columns:
		| ColumnDef<IBrand>[]
		| ColumnDef<ISocial>[]
		| ColumnDef<IPayoutTransactionMinimal>[]
		| ColumnDef<INotificationExtended>[]
		| ColumnDef<ICSVUpload>[]
		| ColumnDef<IAddedFixedPayout>[]
		| ColumnDef<IFixedPayoutSocialsMinimal>[]
		| ColumnDef<ICreator>[]
		| ColumnDef<IVendor>[]
		| ColumnDef<IVendorCampaign>[]
		| ColumnDef<IUser>[]
		| ColumnDef<KycRowData>[];

	setPageNo: React.Dispatch<React.SetStateAction<number>>;
	loading: boolean;
	paginationData?: IPagination;
	sortBy?: string;
	setSortBy?: React.Dispatch<React.SetStateAction<string>>;
	tableType?:
		| "brand"
		| "social"
		| "payout"
		| "notification"
		| "favorites"
		| "fixedPayout"
		| "csv"
		| "creator"
		| "vendor"
		| "vendorCampaigns"
		| "users"
		| "kyc";

	tableBodyClass?: string;
	tableRowClassName?: string;
}

export const DataTableComponents: React.FunctionComponent<AppProps> = ({
	table,
	columns,
	setPageNo,
	loading,
	paginationData,
	sortBy,
	setSortBy,
	tableType,
	tableBodyClass,
	tableRowClassName,
}) => {
	function handleSetSort(e: string) {
		if (setSortBy) {
			setSortBy(e);
		}
	}

	return (
		<div className="relative w-full">
			<div className="rounded-md w-full">
				<Table className="w-full">
					<TableHeader className="relative">
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRow className="flex-1" key={headerGroup.id}>
								{headerGroup.headers.map((header) => {
									return (
										<TableHead
											className="font-semibold text-secondary"
											key={header.id}
										>
											{header.isPlaceholder
												? null
												: flexRender(
														header.column.columnDef.header,
														header.getContext(),
													)}
										</TableHead>
									);
								})}
							</TableRow>
						))}
						{tableType === "social" && (
							<div className="top-0 right-0 absolute mr-4">
								<Select
									onValueChange={(e) => handleSetSort(e)}
									defaultValue={sortBy}
								>
									<SelectTrigger className="gap-2 py-6">
										<span className="text-secondary">Sort by:</span>
										<SelectValue className="" />
									</SelectTrigger>
									<SelectContent className="bg-transparent">
										<SelectGroup className="p-1 font-semibold">
											<SelectItem value="createdAt">Created at</SelectItem>
											<div className="border-t border-border" />
											<SelectItem value="followersCount">Followers</SelectItem>
											<div className="border-t border-border" />
											<SelectItem value="averageViews">
												Average views
											</SelectItem>
											<div className="border-t border-border" />

											<SelectItem value="engagementRate">
												Engagement rate
											</SelectItem>
										</SelectGroup>
									</SelectContent>
								</Select>
							</div>
						)}
					</TableHeader>

					<TableBody className={tableBodyClass}>
						{table.getRowModel().rows?.length ? (
							table.getRowModel().rows.map((row) => (
								<TableRow
									key={row.id}
									data-state={row.getIsSelected() && "selected"}
									className={tableRowClassName}
								>
									{row.getVisibleCells().map((cell) => (
										<TableCell key={cell.id}>
											{flexRender(
												cell.column.columnDef.cell,
												cell.getContext(),
											)}
										</TableCell>
									))}
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell
									colSpan={columns.length}
									className="h-24 text-center"
								>
									No results.
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>
			{paginationData && (
				<PaginationComponent
					fetching={loading}
					paginationData={paginationData}
					onClick={() => setPageNo((prev) => prev + 1)}
				/>
			)}

			{tableType === "creator" ? (
				""
			) : (
				<div className="">
					<Outlet />
				</div>
			)}
		</div>
	);
};
